import { useCallback, useEffect, useRef, useState } from "react";

/** Center a tab within a horizontal scroller without moving the document viewport. */
export function centerElementInHorizontalScroller(
  scroller: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior = "smooth",
): void {
  const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const target = element.offsetLeft + element.offsetWidth / 2 - scroller.clientWidth / 2;
  scroller.scrollTo({
    left: Math.min(maxScroll, Math.max(0, target)),
    behavior,
  });
}

/** Scroll a page section into view — only for explicit user navigation clicks. */
export function scrollSectionIntoView(element: HTMLElement, behavior: ScrollBehavior = "smooth"): void {
  element.scrollIntoView({ behavior, block: "start", inline: "nearest" });
}

const DEFAULT_PROGRAMMATIC_SCROLL_MS = 700;
const DEFAULT_USER_SCROLL_END_MS = 160;

export function useUserScrollGuard(options?: {
  programmaticScrollMs?: number;
  userScrollEndMs?: number;
}) {
  const programmaticUntilRef = useRef(0);
  const userScrollingRef = useRef(false);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const programmaticScrollMs = options?.programmaticScrollMs ?? DEFAULT_PROGRAMMATIC_SCROLL_MS;
  const userScrollEndMs = options?.userScrollEndMs ?? DEFAULT_USER_SCROLL_END_MS;

  useEffect(() => {
    const onScroll = () => {
      if (Date.now() > programmaticUntilRef.current) {
        userScrollingRef.current = true;
      }
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(() => {
        userScrollingRef.current = false;
      }, userScrollEndMs);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    };
  }, [userScrollEndMs]);

  const markProgrammaticScroll = useCallback(() => {
    programmaticUntilRef.current = Date.now() + programmaticScrollMs;
    userScrollingRef.current = false;
  }, [programmaticScrollMs]);

  const isUserScrolling = useCallback(() => userScrollingRef.current, []);

  return { markProgrammaticScroll, isUserScrolling };
}

export function useSectionScrollSpy<T extends string>(input: {
  navItems: Array<{ id: T; label: string }>;
  sectionDomId: (id: T) => string;
  defaultSectionId: T;
}) {
  const { navItems, sectionDomId, defaultSectionId } = input;
  const [activeId, setActiveId] = useState<T>(defaultSectionId);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const { markProgrammaticScroll } = useUserScrollGuard();

  useEffect(() => {
    if (navItems.some((item) => item.id === activeId)) return;
    const fallback = navItems[0]?.id ?? defaultSectionId;
    if (fallback !== activeId) setActiveId(fallback);
  }, [activeId, defaultSectionId, navItems]);

  useEffect(() => {
    const elements = navItems
      .map((item) => document.getElementById(sectionDomId(item.id)))
      .filter((el): el is HTMLElement => Boolean(el));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top ?? 0) - (b.boundingClientRect.top ?? 0));
        const top = visible[0]?.target.id;
        if (!top) return;
        const match = navItems.find((item) => sectionDomId(item.id) === top);
        if (match && match.id !== activeIdRef.current) {
          setActiveId(match.id);
        }
      },
      { rootMargin: "-20% 0px -55% 0px", threshold: [0, 0.1, 0.25] },
    );

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [navItems, sectionDomId]);

  const scrollToSection = useCallback(
    (sectionId: T) => {
      const el = document.getElementById(sectionDomId(sectionId));
      if (!el) return;
      markProgrammaticScroll();
      scrollSectionIntoView(el);
      setActiveId(sectionId);
    },
    [markProgrammaticScroll, sectionDomId],
  );

  return { activeId, scrollToSection };
}
